import { useCallback, useMemo } from "react";

import { FieldSwitch, FormRowField, FormikForm, LandingPageFieldText } from "@skilitics/designer-ui-components";
import { useFormatMessage, type FormatMessages } from "@skilitics/localization";
import { moduleSchema } from "@skilitics/nautilus-endpoint-definition";

import {
  FormBody,
  Header,
  Heading,
  HelpContainer,
  HelpTooltip,
  PageContainer,
  StyledForm,
  SubHeading,
  SwitchLabel,
} from "../../shared";
import { useCreateModuleSettings, type CreateModuleSettings } from "../CreateModuleSettingsContext";

type FormValues = Pick<CreateModuleSettings["metadata"], "intent" | "audience" | "learnerExperience"> &
  Pick<CreateModuleSettings, "adaptToAudience">;

const validationSchema = moduleSchema.pick({ intent: true, audience: true, learnerExperience: true });

export const InformAssistantPage = () => {
  const formatMessage = useFormatMessage(formatStrings);

  const { metadata, updateMetadata, adaptToAudience, setAdaptToAudience } = useCreateModuleSettings();

  const initialValues: FormValues = useMemo(
    () => ({
      intent: metadata.intent,
      audience: metadata.audience,
      learnerExperience: metadata.learnerExperience,
      adaptToAudience,
    }),
    [adaptToAudience, metadata.audience, metadata.intent, metadata.learnerExperience],
  );

  const handleSubmit = useCallback(
    ({ adaptToAudience: adapt, ...rest }: FormValues) => {
      updateMetadata(rest);
      setAdaptToAudience(adapt);
    },
    [setAdaptToAudience, updateMetadata],
  );

  return (
    <PageContainer>
      <Header>
        <Heading>{formatMessage("heading")}</Heading>
        <SubHeading>{formatMessage("subHeading")}</SubHeading>
      </Header>
      <FormikForm<FormValues>
        initialValues={initialValues}
        onSubmit={handleSubmit}
        submitOnBlur
        formatStrings={formatStrings}
        validationSchema={validationSchema}
        dataTest="inform-assistant-form"
      >
        <StyledForm>
          <FormBody>
            <FormRowField<FormValues> name="intent">
              <LandingPageFieldText disabled />
            </FormRowField>

            <FormRowField<FormValues> name="audience">
              <LandingPageFieldText disabled />
            </FormRowField>

            <FormRowField<FormValues> name="learnerExperience">
              <LandingPageFieldText disabled />
            </FormRowField>

            {/* todo: re-enable and implement adapting to the audience */}

            <FormRowField<FormValues> name="adaptToAudience">
              <HelpContainer>
                <FieldSwitch label={<SwitchLabel>{formatMessage("adaptToAudienceLabel")}</SwitchLabel>} disabled />
                <HelpTooltip title={formatMessage("adaptToAudienceDescription")} />
              </HelpContainer>
            </FormRowField>
          </FormBody>
        </StyledForm>
      </FormikForm>
    </PageContainer>
  );
};

const formatStrings = {
  heading: {
    en: "Working with AI",
  },
  subHeading: {
    en: "Let's inform your AI assistant about the scope of this module.",
  },
  intentLabel: {
    en: "The motivations for this training are to...",
  },
  intentDescription: {
    en: "The motivations.",
  },
  intentPlaceholder: {
    en: "Add a motivation...",
  },
  audienceLabel: {
    en: "The age groups this training needs to cater to are...",
  },
  audienceDescription: {
    en: "The age groups.",
  },
  audiencePlaceholder: {
    en: "Add an age group...",
  },
  learnerExperienceLabel: {
    en: "The experience levels of the targeted audience will be...",
  },
  learnerExperienceDescription: {
    en: "The experience levels.",
  },
  learnerExperiencePlaceholder: {
    en: "Add an experience level...",
  },
  adaptToAudienceLabel: {
    en: "I want to adapt the module to my audience",
  },
  adaptToAudienceDescription: {
    en: "Adapt to audience",
  },
} satisfies FormatMessages;
